'use client';

import { Fragment } from 'react';
import Link from 'next/link';
import styles from './ValuesCarousel.module.scss';
import { CheckCircle2, Eye, Scaling, ShieldCheck, Zap, type LucideIcon } from 'lucide-react';
import { gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

interface CarouselValue {
  icon: string;
  title: string;
  description: string;
}

interface ValuesCarouselProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  values?: CarouselValue[];
  ctaLabel?: string;
  ctaHref?: string;
  ctaNote?: string;
  tilt?: 'left' | 'right';
  id?: string;
}

const ICONS: Record<string, LucideIcon> = {
  finish: CheckCircle2,
  transparency: Eye,
  accountability: ShieldCheck,
  scalability: Scaling,
  agility: Zap,
  check: CheckCircle2,
  eye: Eye,
  shield: ShieldCheck,
  bolt: Zap,
  scale: Scaling,
};

const DEFAULT_VALUES: CarouselValue[] = [
  {
    icon: 'finish',
    title: 'Livraison',
    description: 'Chaque livrable est terminé, testé et prêt pour la production.',
  },
  {
    icon: 'transparency',
    title: 'Transparence',
    description: 'Métriques objectives partagées en temps réel, sans filtre.',
  },
  {
    icon: 'accountability',
    title: 'Autonomie',
    description: 'Un interlocuteur unique porte la responsabilité du résultat final.',
  },
  {
    icon: 'agility',
    title: 'Résultat',
    description: 'Les bons profils mobilisés selon vos priorités pour atteindre l’objectif.',
  },
];

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function ValuesCarousel({
  eyebrow = 'NOS ENGAGEMENTS',
  title = 'Ce qui change quand vous travaillez avec nous :',
  highlightedTitle = '*transparence, autonomie, résultat*',
  description = 'Quatre principes qui garantissent une collaboration sereine et une livraison de qualité.',
  values = DEFAULT_VALUES,
  ctaLabel = 'Garanties contractuelles',
  ctaHref = '/contact',
  ctaNote,
  tilt = 'right',
  id,
}: ValuesCarouselProps) {
  useAOS();

  const tiltClass = tilt === 'left' ? styles.tiltLeft : styles.tiltRight;

  return (
    <section id={id} className={`${styles.root} ${tiltClass}`}>
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.inner}>
        <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 className={styles.title}>
            {renderTitle(title)}{' '}
            <span className={styles.highlight}>{renderTitle(highlightedTitle)}</span>
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>

        <div
          className={styles.grid}
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="100"
        >
          {values.map((value) => {
            const Icon = ICONS[value.icon] || CheckCircle2;
            return (
              <article key={value.title} className={styles.valueCard}>
                <div className={styles.iconWrap}>
                  <Icon size={28} strokeWidth={1.6} />
                </div>
                <h3 className={styles.word}>{value.title}</h3>
                <p className={styles.valueDesc}>{value.description}</p>
              </article>
            );
          })}
        </div>

        {ctaLabel && ctaHref && (
          <div
            className={styles.ctaRow}
            data-aos="fade-up"
            data-aos-duration="700"
            data-aos-delay="200"
          >
            <Link
              href={ctaHref}
              className={styles.cta}
              onClick={() => gaCta(ctaLabel, 'values_carousel_cta', ctaHref)}
            >
              {ctaLabel}
            </Link>
            {ctaNote && <p className={styles.ctaNote}>{ctaNote}</p>}
          </div>
        )}
      </div>
    </section>
  );
}
